import React, { useEffect, useState, useMemo, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import API from '../../api';
import { useLang } from '../../LangContext';
import ProductCard from '../../components/bazaar/ProductCard';
import Pagination from '../../components/Pagination';
import { ETHIOPIAN_CITIES, CITY_TRANSLATIONS, categoryImage } from '../../constants';
import { getCartKey } from './cartUtils';

const ORANGE = '#FF6B00';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

// ─── Category icon/color map (same as Home) ───
const CATEGORY_ICONS = {
  'electronics': { icon: '📱', color: '#EEF2FF' },
  'fashion': { icon: '👗', color: '#FEF2F2' },
  'home & living': { icon: '🏠', color: '#FEFCE8' },
  'food & beverage': { icon: '🍽️', color: '#FFF7ED' },
  'beauty & health': { icon: '💄', color: '#FDF2F8' },
  'sports & outdoors': { icon: '⚽', color: '#F0FDF4' },
  'books & media': { icon: '📚', color: '#F5F3FF' },
  'books & education': { icon: '📚', color: '#F5F3FF' },
  'services': { icon: '🔧', color: '#ECFEFF' },
  'clothing': { icon: '👕', color: '#FEF2F2' },
  'food': { icon: '🍽️', color: '#FFF7ED' },
  'health': { icon: '💄', color: '#FDF2F8' },
  'technology': { icon: '💻', color: '#EEF2FF' },
  'automotive': { icon: '🚗', color: '#F0F9FF' },
  'agriculture': { icon: '🌾', color: '#F0FDF4' },
  'construction': { icon: '🏗️', color: '#FFF7ED' },
};
const DEFAULT_CATEGORY_ICON = { icon: '📦', color: '#F3F4F6' };

export default function BazaarPage() {
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    seller_id: '',
    min_price: '',
    max_price: '',
    search: '',
    city: '',
    rating: '',
    inStockOnly: false,
    sellers: [],
    sortBy: 'default',
  });
  const [toast, setToast] = useState('');
  const [bazaarClosed, setBazaarClosed] = useState(false);
  const [openDate, setOpenDate] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [viewMode, setViewMode] = useState('grid');
  const [trendingPeriod, setTrendingPeriod] = useState('week');
  const [page, setPage] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [managedCategories, setManagedCategories] = useState([]);
  const PER_PAGE = 6;
  const { t, lang } = useLang();

  useEffect(() => {
    API.get('/bazaar/settings').then(r => {
      if (r.data?.open_date) setOpenDate(r.data.open_date);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!bazaarClosed || !openDate) return;
    const target = new Date(openDate).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [bazaarClosed, openDate]);

  useEffect(() => {
    API.get('/bazaar/products')
      .then(r => {
        if (r.data?.bazaar_closed) {
          setProducts([]);
          setBazaarClosed(true);
        } else {
          setProducts(Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.products) ? r.data.products : []));
        }
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));

    API.get('/bazaar/products/trending', { params: { period: 'week', limit: 6 } })
      .then(r => setTrending(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});

    API.get('/bazaar/categories')
      .then(r => {
        if (Array.isArray(r.data)) setManagedCategories(r.data.map(c => c.name));
      })
      .catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const seen = new Set();
    products.forEach(p => { if (p.category && !seen.has(p.category)) seen.add(p.category); });
    managedCategories.forEach(c => seen.add(c));
    return Array.from(seen);
  }, [products, managedCategories]);

  const sellers = useMemo(() => {
    const seen = new Set();
    return products
      .filter(p => p.seller_id && !seen.has(p.seller_id) && seen.add(p.seller_id))
      .map(p => ({ id: p.seller_id, name: p.seller_name }));
  }, [products]);

  const cities = useMemo(() => {
    const seen = new Set();
    products.forEach(p => {
      const loc = (p.seller_location || '').trim();
      if (loc) seen.add(loc);
    });
    return Array.from(seen).sort();
  }, [products]);

  const allCities = useMemo(() => {
    const seen = new Set([...ETHIOPIAN_CITIES, ...cities]);
    return Array.from(seen).sort();
  }, [cities]);

  const filtered = useMemo(() => {
    let result = products.filter(p => {
      if (filters.category && p.category.toLowerCase() !== filters.category.toLowerCase()) return false;
      if (filters.seller_id && String(p.seller_id) !== String(filters.seller_id)) return false;
      if (filters.sellers?.length && !filters.sellers.some(s => String(s) === String(p.seller_id))) return false;
      if (filters.city && (p.seller_location || '').toLowerCase() !== filters.city.toLowerCase()) return false;
      if (filters.min_price !== '' && Number(p.price) < Number(filters.min_price)) return false;
      if (filters.max_price !== '' && Number(p.price) > Number(filters.max_price)) return false;
      if (filters.inStockOnly && Number(p.stock_quantity) === 0) return false;
      if (filters.rating && Number(p.rating || 0) < Number(filters.rating)) return false;
      if (filters.search) {
        const term = filters.search.toLowerCase();
        if (!(p.name || '').toLowerCase().includes(term) && !(p.description || '').toLowerCase().includes(term)) return false;
      }
      return true;
    });

    if (filters.sortBy === 'price_asc') result = [...result].sort((a, b) => Number(a.price) - Number(b.price));
    else if (filters.sortBy === 'price_desc') result = [...result].sort((a, b) => Number(b.price) - Number(a.price));
    else if (filters.sortBy === 'name_asc') result = [...result].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (filters.sortBy === 'newest') result = [...result].sort((a, b) => (b.id || 0) - (a.id || 0));

    return result;
  }, [products, filters]);

  const maxPrice = useMemo(() => Math.max(...products.map(p => Number(p.price) || 0), 1000), [products]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const handleAddToCart = (product) => {
    const key = getCartKey(product.seller_id);
    if (!key) return;
    let cart = [];
    try { cart = JSON.parse(localStorage.getItem(key)) || []; } catch { cart = []; }
    if (cart.length > 0 && cart[0].seller_id !== product.seller_id) cart = [];
    const existing = cart.find(item => item.product_id === product.id);
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        photo_url: product.photos?.[0]?.url || null,
        seller_id: product.seller_id,
        seller_name: product.seller_name,
      });
    }
    localStorage.setItem(key, JSON.stringify(cart));
    showToast(`🛒 ${product.name} added to cart!`);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100, color: 'var(--primary)', fontWeight: 700 }}>{t.loading}</div>;
  }

  if (bazaarClosed) {
    const pad = n => String(n).padStart(2, '0');
    return (
      <div className="page" style={{ paddingTop: 100, textAlign: 'center' }}>
        <Reveal>
          <div style={{ fontSize: 72, marginBottom: 24 }}>🛍️</div>
          <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 16 }}>
            Bazaar <span style={{ color: 'var(--primary)' }}>Coming Soon</span>
          </h1>
          <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 48, maxWidth: 600, margin: '0 auto 48px' }}>
            The All Things Bazaar opens during the <strong>last week of every month</strong>. Check back soon to browse and shop from our verified sellers.
          </p>
          {openDate && (
            <div style={{ marginBottom: 48 }}>
              <div style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 14, marginBottom: 24, textTransform: 'uppercase', letterSpacing: '0.1em' }}>⏳ Opens In</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                {[['Days', countdown.days], ['Hours', countdown.hours], ['Minutes', countdown.minutes], ['Seconds', countdown.seconds]].map(([label, val]) => (
                  <div key={label} className="creative-card" style={{ padding: '24px 32px', minWidth: 100, textAlign: 'center' }}>
                    <div style={{ fontSize: 32, fontWeight: 900, color: 'var(--primary)', lineHeight: 1 }}>{pad(val)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8, fontWeight: 700, textTransform: 'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="creative-card" style={{ display: 'inline-block', padding: '24px 40px' }}>
            <div style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 14, marginBottom: 8, textTransform: 'uppercase' }}>📅 Next Bazaar</div>
            <div style={{ color: 'var(--secondary)', fontSize: 20, fontWeight: 900 }}>
              {openDate ? new Date(openDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Last week of this month'}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 15, marginTop: 4 }}>Open for one week only</div>
          </div>
        </Reveal>
      </div>
    );
  }

  const activeCatKey = filters.category ? filters.category.toLowerCase() : '';
  const activeCatInfo = activeCatKey ? (CATEGORY_ICONS[activeCatKey] || DEFAULT_CATEGORY_ICON) : null;

  const clearAllFilters = () => setFilters({
    category: '', seller_id: '', min_price: '', max_price: '', search: '',
    city: '', rating: '', inStockOnly: false, sellers: [], sortBy: 'default',
  });

  return (
    <div style={{ paddingTop: 0, background: '#fff', minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* ═══════════════════ HERO (1920×380) ═══════════════════ */}
      <section style={{
        position: 'relative', overflow: 'hidden', background: '#f5f0eb',
        width: '100%', maxWidth: '100%', height: 380,
      }}>
        <img
          src="/images/bazzar.png"
          alt="Bazaar"
          onLoad={() => setImgLoaded(true)}
          style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center center',
            opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.6s ease',
          }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 40%, rgba(255,255,255,0.5) 60%, rgba(255,255,255,0) 65%)',
        }} />
        <div style={{
          position: 'relative', zIndex: 2, maxWidth: 1200, margin: '0 auto',
          height: '100%', padding: '0 32px',
          display: 'flex', alignItems: 'center',
        }}>
          <div style={{ maxWidth: 520, width: '60%' }}>
            <Reveal>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(245,130,32,0.1)', color: '#F58220', padding: '6px 16px', borderRadius: 100, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>
                Ethiopian Marketplace
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 style={{ fontSize: 'clamp(24px, 3.5vw, 34px)', fontWeight: 900, lineHeight: 1.1, color: '#0B1B33', marginBottom: 10, margin: 0 }}>
                {t.bazaarTitle || 'Bazaar'} <span style={{ color: '#F58220' }}>Marketplace</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.6, margin: '10px 0 20px' }}>
                {t.bazaarSubtitle || 'Discover authentic products from verified Ethiopian sellers — from traditional crafts to modern essentials.'}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════ BODY ═══════════════════ */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 56px' }}>
        {/* ─── Breadcrumb ─── */}
        <nav style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 16 }}>
          <Link to="/" style={{ color: '#9CA3AF', textDecoration: 'none', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = ORANGE} onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: ORANGE, fontWeight: 700 }}>Bazaar</span>
        </nav>

        {/* ─── Header: Title + Stat cards ─── */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 900, color: '#1F2937', margin: '0 0 6px' }}>
              Bazaar
            </h1>
            <p style={{ color: '#6B7280', fontSize: 15, margin: 0 }}>
              Shop from verified Ethiopian sellers — handcrafted goods, electronics, fashion & more.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, auto)', gap: 12 }}>
            <StatCard value={products.length} label="Products" icon="🛍️" gradient="linear-gradient(135deg,#FF6B00,#FFA94D)" />
            <StatCard value={sellers.length} label="Sellers" icon="🏪" gradient="linear-gradient(135deg,#6366F1,#A5B4FC)" />
            <StatCard value={categories.length} label="Categories" icon="🏷️" gradient="linear-gradient(135deg,#10B981,#6EE7B7)" />
            <StatCard value={cities.length} label="Cities" icon="📍" gradient="linear-gradient(135deg,#F59E0B,#FCD34D)" />
          </div>
        </div>

        {/* ─── Search & Filter bar ─── */}
        <div style={{
          background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB',
          padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.04)', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={filters.category} onChange={e => { setFilters(f => ({ ...f, category: e.target.value })); setPage(1); }}
              style={barInput}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.city} onChange={e => { setFilters(f => ({ ...f, city: e.target.value })); setPage(1); }}
              style={barInput}>
              <option value="">All Locations</option>
              {allCities.map(c => (
                <option key={c} value={c}>{CITY_TRANSLATIONS[lang]?.[c] || c}</option>
              ))}
            </select>
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                placeholder="Search products..."
                value={filters.search}
                onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
                style={{ ...barInput, padding: '12px 14px 12px 42px', width: '100%' }}
              />
            </div>
            <button onClick={() => setPage(1)}
              style={{
                padding: '12px 28px', borderRadius: 10, border: 'none', background: ORANGE, color: '#fff',
                fontWeight: 800, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
                boxShadow: '0 6px 16px rgba(255,107,0,0.25)', transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >Search</button>
            <button onClick={clearAllFilters}
              style={{
                padding: '12px 22px', borderRadius: 10, border: '1px solid #E5E7EB', background: '#fff',
                color: '#4B5563', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap',
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = ORANGE; e.currentTarget.style.color = ORANGE; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#4B5563'; }}
            >Reset</button>
          </div>
        </div>

        {/* ─── Category nav (horizontal scroll) ─── */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 20, scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          <CatNavItem label="All Categories" icon="✨" color="#F3F4F6" active={!filters.category} onClick={() => { setFilters(f => ({ ...f, category: '' })); setPage(1); }} />
          {categories.map(c => {
            const info = CATEGORY_ICONS[c.toLowerCase()] || DEFAULT_CATEGORY_ICON;
            return (
              <CatNavItem key={c} label={c} icon={info.icon} color={info.color} img={categoryImage(c)}
                active={filters.category === c}
                onClick={() => { setFilters(f => ({ ...f, category: f.category === c ? '' : c })); setPage(1); }} />
            );
          })}
        </div>

        {/* ─── Two-column layout ─── */}
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          {/* Filter sidebar */}
          <aside style={{ flex: '0 0 210px', width: 210, position: 'sticky', top: 90 }}>
            <div style={{
              background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <h3 style={{ fontSize: 16, fontWeight: 900, color: '#1F2937', margin: 0 }}>Filters</h3>
                <button onClick={clearAllFilters} style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Clear All</button>
              </div>

              {/* Price range slider */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', marginBottom: 12 }}>Price Range (ETB)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 800, color: ORANGE, marginBottom: 8 }}>
                  <span>ETB {filters.min_price === '' ? 0 : Number(filters.min_price).toLocaleString()}</span>
                  <span>ETB {filters.max_price === '' ? Number(maxPrice).toLocaleString() : Number(filters.max_price).toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={maxPrice}
                  step={10}
                  value={filters.max_price === '' ? maxPrice : Number(filters.max_price)}
                  onChange={e => setFilters(f => ({ ...f, max_price: e.target.value === String(maxPrice) ? '' : e.target.value }))}
                  style={{ width: '100%', accentColor: ORANGE, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <input type="number" min="0" placeholder="Min"
                    value={filters.min_price}
                    onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))}
                    style={barInput} />
                  <input type="number" min="0" placeholder="Max"
                    value={filters.max_price}
                    onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))}
                    style={barInput} />
                </div>
              </div>

              {/* Availability */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', marginBottom: 10 }}>Availability</div>
                <label style={checkLabel}>
                  <input type="checkbox" checked={filters.inStockOnly}
                    onChange={e => setFilters(f => ({ ...f, inStockOnly: e.target.checked }))} style={checkbox} />
                  In Stock Only
                </label>
              </div>

              {/* City dropdown */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', marginBottom: 10 }}>Location</div>
                <select value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} style={{ ...barInput, width: '100%' }}>
                  <option value="">All Locations</option>
                  {allCities.map(c => (
                    <option key={c} value={c}>{CITY_TRANSLATIONS[lang]?.[c] || c}</option>
                  ))}
                </select>
              </div>

              {/* Sellers */}
              {sellers.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', marginBottom: 10 }}>Sellers</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 160, overflowY: 'auto' }}>
                    {sellers.map(s => (
                      <label key={s.id} style={checkLabel}>
                        <input type="checkbox"
                          checked={(filters.sellers || []).some(x => String(x) === String(s.id))}
                          onChange={e => {
                            const cur = filters.sellers || [];
                            setFilters(f => ({
                              ...f,
                              sellers: e.target.checked ? [...cur, s.id] : cur.filter(x => String(x) !== String(s.id)),
                            }));
                          }}
                          style={checkbox} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Rating */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#1F2937', marginBottom: 10 }}>Rating</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[4, 3, 2].map(r => (
                    <button key={r}
                      onClick={() => setFilters(f => ({ ...f, rating: f.rating === String(r) ? '' : String(r) }))}
                      style={{
                        padding: '6px 12px', borderRadius: 100, border: '1px solid',
                        borderColor: filters.rating === String(r) ? ORANGE : '#E5E7EB',
                        background: filters.rating === String(r) ? `${ORANGE}0F` : '#fff',
                        color: filters.rating === String(r) ? ORANGE : '#6B7280',
                        fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >{'★'.repeat(r)}{'☆'.repeat(5 - r)}</button>
                  ))}
                </div>
              </div>

              {/* Apply Filters */}
              <button onClick={() => setPage(1)}
                style={{
                  width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  background: ORANGE, color: '#fff', fontWeight: 800, fontSize: 14,
                  cursor: 'pointer', boxShadow: '0 6px 16px rgba(255,107,0,0.2)',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.08)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
              >Apply Filters</button>
            </div>
          </aside>

          {/* Product area */}
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            {/* Toolbar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 12, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #E5E7EB',
            }}>
              <span style={{ color: '#6B7280', fontSize: 14, fontWeight: 700 }}>
                {filtered.length === products.length ? `${filtered.length} products` : `${filtered.length} of ${products.length} products`}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <select value={filters.sortBy}
                  onChange={e => { setFilters(f => ({ ...f, sortBy: e.target.value })); setPage(1); }}
                  style={selectStyle}>
                  <option value="default">Sort: Default</option>
                  <option value="newest">Newest First</option>
                  <option value="price_asc">Price: Low → High</option>
                  <option value="price_desc">Price: High → Low</option>
                  <option value="name_asc">Name: A → Z</option>
                </select>
                <div style={{ display: 'flex', background: '#F9FAFB', borderRadius: 10, padding: 4, border: '1px solid #E5E7EB' }}>
                  <button onClick={() => setViewMode('grid')}
                    style={{ ...viewBtnStyle, background: viewMode === 'grid' ? '#fff' : 'transparent', boxShadow: viewMode === 'grid' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>⊞</button>
                  <button onClick={() => setViewMode('list')}
                    style={{ ...viewBtnStyle, background: viewMode === 'list' ? '#fff' : 'transparent', boxShadow: viewMode === 'list' ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>☰</button>
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                <div style={{ fontSize: 64, marginBottom: 24 }}>🔍</div>
                <p style={{ color: '#6B7280', marginBottom: 16 }}>{t.noResults || 'No products found'}</p>
                <button onClick={clearAllFilters} style={{ color: ORANGE, background: 'none', border: 'none', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                <div className={viewMode === 'grid' ? 'bazaar-grid' : ''} style={viewMode === 'list' ? listStyle : {}}>
                  {filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((product, idx) => (
                    <Reveal key={product.id} delay={Math.min(idx * 0.03, 0.3)}>
                      <ProductCard product={product} onAddToCart={handleAddToCart} viewMode={viewMode} />
                    </Reveal>
                  ))}
                </div>
                <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PER_PAGE)} onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
              </>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} style={toastStyle} role="status">
          {toast}
        </motion.div>
      )}

      <style>{`
        .bazaar-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
        @media (max-width: 900px) {
          aside[style*="flex: 0 0 210px"] { flex: 1 1 100% !important; width: 100% !important; position: relative !important; top: 0 !important; }
          [style*="display: flex"][style*="gap: 24"] { flex-direction: column !important; }
          .bazaar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          section[style*="height: 380"] { height: 260px !important; }
          [style*="maxWidth: 520"][style*="width: 60%"] { width: 85% !important; max-width: 100% !important; }
        }
        @media (max-width: 480px) {
          section[style*="height: 380"] { height: 220px !important; }
          .bazaar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
          [style*="grid-template-columns: repeat(4, auto)"] { grid-template-columns: repeat(2, 1fr) !important; }
          [style*="maxWidth: 1280"] { padding: 20px 12px 60px !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── Stat Card ─── */
const StatCard = ({ value, label, icon, gradient }) => (
  <div style={{
    background: '#fff', borderRadius: 14, border: '1px solid #E5E7EB', padding: '14px 18px',
    display: 'flex', alignItems: 'center', gap: 12, minWidth: 110,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)', transition: 'all 0.3s', cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.03)'; }}
  >
    <div style={{
      width: 40, height: 40, borderRadius: 12, background: gradient, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    }}>{icon}</div>
    <div>
      <div style={{ fontSize: 20, fontWeight: 900, color: '#1F2937', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  </div>
);

/* ─── Category nav item ─── */
const CatNavItem = ({ label, icon, color, img, active, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flexShrink: 0,
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px', fontFamily: 'inherit',
  }}>
    {img ? (
      <span style={{
        width: 52, height: 52, borderRadius: '50%', overflow: 'hidden', display: 'inline-flex',
        transition: 'all 0.3s', boxShadow: active ? `0 0 0 3px ${ORANGE}30` : '0 2px 8px rgba(0,0,0,0.05)',
        transform: active ? 'scale(1.05)' : 'scale(1)',
      }}>
        <img src={img} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </span>
    ) : (
      <span style={{
        width: 52, height: 52, borderRadius: '50%', background: color, fontSize: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s', boxShadow: active ? `0 0 0 3px ${ORANGE}30` : '0 2px 8px rgba(0,0,0,0.05)',
        transform: active ? 'scale(1.05)' : 'scale(1)',
      }}>{icon}</span>
    )}
    <span style={{
      fontSize: 11, fontWeight: 700, color: active ? ORANGE : '#4B5563', whiteSpace: 'nowrap',
      borderBottom: active ? `2px solid ${ORANGE}` : '2px solid transparent',
      paddingBottom: 2, transition: 'all 0.3s',
    }}>{label}</span>
  </button>
);

const barInput = {
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid #E5E7EB',
  background: '#F9FAFB',
  color: '#1F2937',
  fontSize: 14,
  fontWeight: 600,
  outline: 'none',
  fontFamily: 'inherit',
  minWidth: 120,
};

const checkLabel = {
  display: 'flex', alignItems: 'center', gap: 8,
  fontSize: 13, fontWeight: 600, color: '#4B5563', cursor: 'pointer',
};

const checkbox = { accentColor: ORANGE, width: 16, height: 16, cursor: 'pointer' };

const selectStyle = {
  padding: '10px 16px',
  borderRadius: 10,
  background: '#fff',
  border: '1px solid #E5E7EB',
  color: '#1F2937',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
  outline: 'none',
  fontFamily: 'inherit',
};

const viewBtnStyle = {
  width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 18,
  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', color: '#1F2937',
};

const listStyle = {
  display: 'flex', flexDirection: 'column', gap: 20,
};

const toastStyle = {
  position: 'fixed', bottom: 40, right: 40, background: '#1F2937', color: '#fff',
  padding: '16px 32px', borderRadius: 20, fontWeight: 800, fontSize: 16, zIndex: 9999,
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
};
